<template>
    <div class="users-container">
        <!-- Filtros Avançados -->
        <AdvancedFilters 
            ref="advancedFilters"
            :show-date-filter="false"
            :show-course-filter="false"
            :show-user-filter="true"
            :show-email-filter="true"
            :show-cpf-filter="true"
            @filters-applied="onFiltersApplied"
            @filters-cleared="onFiltersCleared"
        />

        <Loader v-if="!hasReady" />
        <div v-else>
            <v-table class="custom-table table-with-filters">
                <thead>
                    <tr>
                        <th style="width: 20%; text-align: left;" class="sortable-header" @click="sortBy('name')">
                            {{ $t('admin.reports.columns.name') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 20%; text-align: left;" class="sortable-header" @click="sortBy('email')">
                            {{ $t('admin.reports.columns.email') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 15%; text-align: left;" class="sortable-header" @click="sortBy('phone')">
                            {{ $t('admin.reports.columns.phone') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 15%; text-align: left;" class="sortable-header" @click="sortBy('socialNumber')">
                            {{ $t('admin.reports.columns.cpf') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 20%; text-align: left;" class="sortable-header" @click="sortBy('address')">
                            {{ $t('admin.reports.columns.address') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 5%; text-align: left;" class="sortable-header" @click="sortBy('county')">
                            {{ $t('admin.reports.columns.state') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                        <th style="width: 5%; text-align: left;" class="sortable-header" @click="sortBy('city')">
                            {{ $t('admin.reports.columns.city') }}
                            <fa icon="fa-solid fa-sort" class="sort-icon" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in paginatedUsers" :key="user.id">
                        <td style="width: 20%; text-align: left;">{{ user.name }}</td>
                        <td style="width: 20%; text-align: left;">{{ user.email }}</td>
                        <td style="width: 15%; text-align: left;">{{ user.phone }}</td>
                        <td style="width: 15%; text-align: left;">{{ user.socialNumber }}</td>
                        <td style="width: 20%; text-align: left;">{{ user.address }}</td>
                        <td style="width: 5%; text-align: left;">{{ user.county }}</td>
                        <td style="width: 5%; text-align: left;">{{ user.city }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <div class="pagination users-pagination">
            <v-responsive>
                <v-pagination
                    v-model="uiCurrentPage"
                    :length="totalPages"
                    :total-visible="5"
                    :disabled="!hasReady"
                    @update:model-value="onPageChange"
                ></v-pagination>
            </v-responsive>
        </div>
    </div>
</template>

<script>
import Loader from '@/components/Loader/loader.vue';
import AdvancedFilters from '@/components/Admin/Reports/AdvancedFilters.vue';
import api from "@/utils/reports.js";
import { mapState } from "vuex";
import { toast } from 'vue3-toastify';

export default {
    data() {
        return {
            label: this.labels,
            userList: [],
            filteredUsers: [],
            searchTerm: '',
            status: 'all',
            currentPage: 0, 
            pageSize: 10, 
            totalPages: 0,
            totalElements: 0,
            alertOpenCourse: false,
            removedcoursename: '',
            afterWidth: 40,
            openModalCreateWarning: false,
            openModalEditWarning: false,
            hasReady: false,
            indiceItem: [false, false, false, true],
            isMobileIcon: false,
            isLoading: false,
            currentSort: { field: '', order: 'asc' },
            activeFilters: {}, // Armazenar filtros ativos para export
        };
    },
    props: {
        labels: Array
    },
    computed: {
        ...mapState(['token', 'numberIntensTable']),
        uiCurrentPage() {
            return this.currentPage + 1; // Convert 0-based to 1-based for UI
        },
        paginatedUsers() {
            // A paginação agora é feita no backend, então retornamos todos os usuários recebidos
            if (!Array.isArray(this.filteredUsers)) {
                return [];
            }
            return this.filteredUsers;
        }
    },
    methods: {
        async exportToCSV() {
            try {
                toast.info(this.$t('admin.reports.exporting'));
                this.isLoading = true;
                
                // Preparar filtros para a API de export
                const exportFilters = {};
                
                if (this.activeFilters.name) {
                    exportFilters.name = this.activeFilters.name;
                }
                if (this.activeFilters.email) {
                    exportFilters.email = this.activeFilters.email;
                }
                if (this.activeFilters.courseName) {
                    exportFilters.courseName = this.activeFilters.courseName;
                }
                if (this.activeFilters.userCpf || this.activeFilters.socialNumber) {
                    exportFilters.socialNumber = this.activeFilters.userCpf || this.activeFilters.socialNumber;
                }
                
                // Chamar endpoint de export
                const response = await api.getDataReportUserExport(this.token, exportFilters);
                
                // Fazer download do arquivo blob
                const url = window.URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_usuarios.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(this.$t('admin.reports.exportSuccess'));
            } catch (error) {
                toast.error(this.$t('admin.reports.exportError'));
                console.error('Erro ao exportar CSV:', error);
            } finally {
                this.isLoading = false;
            }
        },
        
        async getDataReport(searchTerm = '') {
            try {
                this.hasReady = false;
                this.isLoading = true;
                
                const filters = {};
                
                // Se houver searchTerm, buscar por nome do usuário
                if (searchTerm && searchTerm.trim() !== '') {
                    filters.name = searchTerm.trim();
                }
                
                // Aplicar filtros ativos se existirem
                if (this.activeFilters.name) {
                    filters.name = this.activeFilters.name;
                }
                if (this.activeFilters.email) {
                    filters.email = this.activeFilters.email;
                }
                if (this.activeFilters.socialNumber) {
                    filters.socialNumber = this.activeFilters.socialNumber;
                }
                
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                console.log('users.vue - buscando dados com filtros:', filters, 'pagination:', pagination);
                
                const response = await api.getDataReportUser(
                    this.token, 
                    filters,
                    pagination
                );
                
                console.log('users.vue - resposta da API:', response);
                
                // Processar resposta da API (paginação do backend)
                const userList = response?.userList || response?.content || [];
                this.userList = userList.filter(user => user.type !== 1 && user.email != "");
                this.filteredUsers = [...this.userList];
                
                // Usar dados de paginação do backend
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                this.currentPage = response?.currentPage || response?.number || this.currentPage;
                this.pageSize = response?.pageSize || response?.size || this.pageSize;
                
                this.hasReady = true;
                
                // Mostrar mensagem se não houver resultados
                if (this.userList.length === 0 && (searchTerm || Object.keys(filters).length > 0)) {
                    toast.info(this.$t('admin.reports.noSearchResults'));
                }
            } catch (error) {
                toast.error(this.$t('admin.reports.loadError'));
                console.error('Erro Report Users', error);
                // Garantir que mesmo em caso de erro, os dados sejam limpos
                this.userList = [];
                this.filteredUsers = [];
                this.totalPages = 0;
                this.totalElements = 0;
                this.hasReady = true;
            } finally {
                this.isLoading = false;
            }
        },

        async previousPage() {
            if (this.currentPage > 0) {
                this.currentPage--;
                await this.getDataReport();
            }
        },

        async nextPage() {
            if (this.currentPage < this.totalPages - 1) {
                this.currentPage++;
                await this.getDataReport();
            }
        },

        async onPageChange(page) {
            this.currentPage = page - 1; // Convert to 0-based index for API
            await this.getDataReport();
        },
        
        async searchUsers() {
            this.currentPage = 0;
            await this.getDataReport();
        },

        async changePageSize(newSize) {
            this.pageSize = newSize;
            this.currentPage = 0;
            await this.getDataReport();
        },
        onFiltersApplied(filters) {
            this.applyAdvancedFilters(filters);
        },
        onFiltersCleared() {
            this.clearAdvancedFilters();
        },
        async applyAdvancedFilters(filters) {
            try {
                this.hasReady = false;
                this.isLoading = true;
                
                // Armazenar filtros ativos para export
                this.activeFilters = { ...filters };
                if (filters.userName) this.activeFilters.name = filters.userName;
                if (filters.userEmail) this.activeFilters.email = filters.userEmail;
                if (filters.userCpf) this.activeFilters.socialNumber = filters.userCpf;
                
                // Preparar filtros para a API
                const apiFilters = {};
                
                // Filtro por nome do usuário
                if (filters.userName && filters.userName.trim() !== '') {
                    apiFilters.name = filters.userName.trim();
                }
                
                // Filtro por email
                if (filters.userEmail && filters.userEmail.trim() !== '') {
                    apiFilters.email = filters.userEmail.trim();
                }
                
                // Filtro por CPF
                if (filters.userCpf && filters.userCpf.trim() !== '') {
                    apiFilters.socialNumber = filters.userCpf.trim();
                }
                
                // Resetar para primeira página quando aplicar filtros
                this.currentPage = 0;
                
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                // Buscar dados da API com os filtros e paginação
                console.log('Aplicando filtros via API (usuários):', apiFilters, 'pagination:', pagination);
                const response = await api.getDataReportUser(
                    this.token, 
                    apiFilters,
                    pagination
                );
                
                // Processar resposta da API (paginação do backend)
                const userList = response?.userList || response?.content || [];
                let filteredData = userList.filter(user => user.type !== 1 && user.email != "");
                this.userList = filteredData;
                this.filteredUsers = filteredData;
                
                // Usar dados de paginação do backend
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                this.currentPage = response?.currentPage || response?.number || 0;
                this.pageSize = response?.pageSize || response?.size || this.pageSize;
                
                this.hasReady = true;
                
                // Mostrar mensagem se não houver resultados
                if (filteredData.length === 0) {
                    toast.info(this.$t('admin.reports.noFilterResults'));
                }
                
            } catch (error) {
                toast.error(this.$t('admin.reports.filtersApplyError'));
                console.error('Erro ao aplicar filtros:', error);
                // Garantir que mesmo em caso de erro, os dados sejam limpos
                this.filteredUsers = [];
                this.userList = [];
                this.totalPages = 0;
                this.totalElements = 0;
                this.hasReady = true;
            } finally {
                this.isLoading = false;
            }
        },
        async clearAdvancedFilters() {
            try {
                this.hasReady = false;
                this.isLoading = true;
                
                // Limpar filtros ativos
                this.activeFilters = {};
                
                // Resetar para primeira página
                this.currentPage = 0;
                
                const filters = {};
                const pagination = {
                    page: this.currentPage,
                    size: this.pageSize
                };
                
                // Buscar todos os dados novamente sem filtros
                const response = await api.getDataReportUser(
                    this.token, 
                    filters,
                    pagination
                );
                
                const userList = response?.userList || response?.content || [];
                this.userList = userList.filter(user => user.type !== 1 && user.email != "");
                this.filteredUsers = [...this.userList];
                
                // Usar dados de paginação do backend
                this.totalPages = response?.totalPages || 0;
                this.totalElements = response?.totalElements || 0;
                this.currentPage = response?.currentPage || response?.number || 0;
                this.pageSize = response?.pageSize || response?.size || this.pageSize;
                
                this.hasReady = true;
            } catch (error) {
                console.error('Erro ao limpar filtros:', error);
                this.filteredUsers = [];
                this.userList = [];
                this.totalPages = 0;
                this.totalElements = 0;
                this.hasReady = true;
            } finally {
                this.isLoading = false;
            }
        },
        sortBy(field) {
            if (this.currentSort.field === field) {
                this.currentSort.order = this.currentSort.order === 'asc' ? 'desc' : 'asc';
            } else {
                this.currentSort.field = field;
                this.currentSort.order = 'asc';
            }
            
            this.filteredUsers = this.sortData([...this.filteredUsers], field, this.currentSort.order);
        },
        sortData(data, sortBy, sortOrder) {
            return data.sort((a, b) => {
                let aValue, bValue;
                
                switch (sortBy) {
                    case 'name':
                        aValue = a.name.toLowerCase();
                        bValue = b.name.toLowerCase();
                        break;
                    case 'email':
                        aValue = a.email.toLowerCase();
                        bValue = b.email.toLowerCase();
                        break;
                    case 'phone':
                        aValue = a.phone || '';
                        bValue = b.phone || '';
                        break;
                    case 'socialNumber':
                        aValue = a.socialNumber || '';
                        bValue = b.socialNumber || '';
                        break;
                    case 'address':
                        aValue = a.address || '';
                        bValue = b.address || '';
                        break;
                    case 'county':
                        aValue = a.county || '';
                        bValue = b.county || '';
                        break;
                    case 'city':
                        aValue = a.city || '';
                        bValue = b.city || '';
                        break;
                    default:
                        return 0;
                }
                
                if (sortOrder === 'asc') {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });
        },
    },
    
    components: {
        Loader,
        AdvancedFilters
    },
    
    async created() {
        await this.getDataReport();
    },
};
</script>

<style scoped>

.search-form-input {
    position: relative;
    width: 100%;
}
.title-container {
    position: relative;
    margin-bottom: 10px;
    padding-bottom: 0;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
}

.title-container .title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 0.5rem;
}

.title-container .subtitle {
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.4;
    margin-bottom: 1rem;
}

.export-button-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
    margin-top: 1rem;
}

.export-button-container .btn-weflix {
    background-color: #2f2e8b;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
    border: none;
}

.export-button-container .btn-weflix:hover:not(:disabled) {
    background-color: #23225f;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(47, 46, 139, 0.3);
}

.export-button-container .btn-weflix:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.export-button-container .btn-weflix svg {
    font-size: 1rem;
}

.search-row {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
}

.search-row .form-group {
    flex: 1;
}

.btn-weflix.absolute {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #2f2e8b;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(47, 46, 139, 0.2);
}

.btn-weflix.absolute:hover {
    background-color: #23225f;
    transform: translateY(-2px);
}

.users-table-responsive {
    background-color: #ffffff;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.users-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.users-table th {
    background-color: #f8fafc;
    padding: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    text-align: left;
    border-bottom: 2px solid #e2e8f0;
}

.users-table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-bottom: 1px solid #f0f0f0;
    color: #4a5568;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.users-table tr:hover {
    background-color: #f8fafc;
}

.badge-active, .badge-inactive {
    padding: 0.25rem 0.75rem;
    border-radius: 5px;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-block;
    text-align: center;
    transition: all 0.2s ease;
    width: 70%;
}

.badge-active {
    background-color: #7bcc8180;
    color: #4b614e;
}

.badge-inactive {
    background-color: #d7d7ee80;
    color: #2f2e8b;
}

.user-btn-edit {
    padding: 0.4rem;
    margin: 0 0.25rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: #2f2e8b;
}

.search-form-input {
    position: relative;
}

.search-form-control {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.search-form-control:focus {
    outline: none;
    border-color: #2f2e8b;
    box-shadow: 0 0 0 3px rgba(47, 46, 139, 0.1);
}

.search-input-icon {
    position: absolute;
    right: 0.75rem;
    top: 45%;
    transform: translateY(-50%);
    color: #9ca3af;
    cursor: pointer;
}

.users-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-bottom: 10px;
}

.users-page-btn {
    padding: 0.5rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: white;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s;
}

.users-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.users-page-btn:not(:disabled):hover {
    background: #f8fafc;
    border-color: #cbd5e0;
}

.users-page-info {
    color: #4a5568;
    font-size: 0.875rem;
}

@media (max-width: 768px) {

    .title-container {
        margin-bottom: 1.5rem;
    }

    .search-row {
        flex-direction: column;
    }

    .users-table th, .users-table td {
        padding: 0.5rem;
    }
}

@media (max-width: 600px) {
    .btn-weflix.absolute {
        padding: 0.5rem;
    }
    
    .btn-weflix.absolute span {
        display: none;
    }

    .users-table-responsive {
        overflow-x: auto;
    }
}

select.form-control.filter-form-control {
    width: 60%;
}
.form-group.filter-form-group {
    justify-content: space-around;
    align-items: center;
    margin: 0 !important;
}
.form-group.search-form-group {
    margin: 0 !important;
}
table.table.users-table {
    border: none !important;
    padding: 0 !important;
}
button.btn-edit.user-btn-edit svg {
    color: #2f2e8b;
}
.form-group.filter-form-group {
    display: flex;
}
.btn-weflix.absolute span.flex {
    font-size: 16px;
}
.btn-weflix.absolute svg {
    color: white;
    font-size: 15px;
}
/* DARK */
.dashboard.dark .users-table-responsive {
    background-color: #000 !important;
}
.dashboard.dark .weeducAdminReports .page-info.users-page-info {
    color: white;
}
.dashboard.dark .weeducAdminReports .users-table th {
    border-bottom: 2px solid #575757 !important;
}
.dashboard.dark .weeducAdminReports .users-table td {
    border-bottom: 2px solid #575757 !important;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    position: relative;
}

.sortable-header:hover {
    background-color: #f1f3f4;
}

.sort-icon {
    margin-left: 0.5rem;
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.sortable-header:hover .sort-icon {
    opacity: 1;
}

.dashboard.dark .sortable-header:hover {
    background-color: #666;
}

.table-with-filters {
    border-radius: 0 0 6px 6px;
    border: 1px solid #e2e8f0;
    border-top: none;
    overflow: hidden;
}

.dashboard.dark .table-with-filters {
    border-color: #444;
}
</style>
